/**
 * طبقة الاتصال بمكتب الحسابات —
 * `GET /finance/summary` · `/payments` · `/teacher-payouts` · `/staff-payouts`
 * و`POST /payments/{id}/link` · `/unlink`.
 *
 * 🔴 **السؤال اللي الشاشة موجودة عشانه:** ٣٨٧ دفعة من ٤٠٦ دخلت السيستم
 *    **بلا أسرة** ($43,741)، لأن عمود اسم الدافع في الشيت كنية حرّة مش
 *    مفتاح. الربط قرار إنسان، والسيستم بيعمّم الهجاء بعده.
 *
 * ⛔ القواعد اللي الملف ده بيحرسها:
 *
 *   ① **الفلوس نصوص، مش أرقام.** الرد بيبعت المبالغ نصًّا و`Number()`
 *      هنا كان هيرجّعنا لـ`float` — يعني كسور ضايعة في تقرير فلوس.
 *      العرض بيتنسّق من النص، والمقارنة والجمع بيحصلوا في السيرفر.
 *
 *   ② **ممنوع الفشل الصامت.** أي رد مش قائمة/مش كائن بيرمي خطأ صريح
 *      ومعاه المسار في السجل — نفس `api/journeys.ts` بالحرف.
 *
 *   ③ **`null` مش صفر.** `account_raw = null` معناها «محجوبة عنك» —
 *      و`account_present` هو اللي بيقول هل فيه رقم أصلًا ولا لأ. الخلط
 *      بينهم بيخلّي الشاشة تقول «مفيش رقم حساب» وهي مش شايفاه بس.
 */
import { ApiError, api } from "./client";
import { latinDigits } from "./registry";

type Raw = Record<string, unknown>;

function fail(path: string, reason: string): never {
  console.error(`[finance] ${reason} (${path})`);
  throw new ApiError(
    502,
    { code: "EMPTY_RESPONSE", message_ar: "السيستم رجّع رد مش مفهوم — مقدرناش نعرض الأرقام" },
    null,
  );
}

/**
 * ⚠️ **نقطة فرض قاعدة الأرقام اللاتينية** — نفس نمط `api/registry.ts`.
 * ⛔ ما تستبدلهاش بـ`String(v)`؛ القاعدة بتتكسر بصمت.
 */
function str(v: unknown): string {
  return typeof v === "string" ? latinDigits(v) : "";
}
function strOrNull(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? latinDigits(v) : null;
}
function num(v: unknown, fallback = 0): number {
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}
function obj(v: unknown): Raw {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Raw) : {};
}
function list(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}

export type FinanceQuality = {
  needs_review: boolean;
  issues: string[];
  issues_ar: string[];
  source: { sheet: string | null; row: number | null } | null;
};

function quality(v: unknown): FinanceQuality {
  const q = obj(v);
  const s = q["source"] ? obj(q["source"]) : null;
  return {
    needs_review: q["needs_review"] === true,
    issues: list(q["issues"]).map(str).filter(Boolean),
    issues_ar: list(q["issues_ar"]).map(str).filter(Boolean),
    source: s ? { sheet: strOrNull(s["sheet"]), row: typeof s["row"] === "number" ? s["row"] : null } : null,
  };
}

/* ═════════════════════════════ المتحصلات ═════════════════════════════ */

export type PaymentRow = {
  id: string;
  month: string;
  paid_at: string | null;
  payer_name_raw: string;
  family_id: string | null;
  family_name_ar: string | null;
  /** ⚠️ **نص** — الفلوس ما بتتحولش لرقم في الواجهة */
  amount: string;
  currency: string;
  channel: string;
  channel_ar: string;
  received_by_raw: string | null;
  status_ar: string;
  status_raw: string | null;
  notes_ar: string | null;
  source_row_no: number | null;
  data_quality: FinanceQuality;
};

function paymentRow(v: unknown): PaymentRow {
  const r = obj(v);
  return {
    id: str(r["id"]),
    month: str(r["month"]),
    paid_at: strOrNull(r["paid_at"]),
    payer_name_raw: str(r["payer_name_raw"]),
    family_id: strOrNull(r["family_id"]),
    family_name_ar: strOrNull(r["family_name_ar"]),
    amount: str(r["amount"]) || "0",
    currency: str(r["currency"]) || "USD",
    channel: str(r["channel"]),
    channel_ar: str(r["channel_ar"]),
    received_by_raw: strOrNull(r["received_by_raw"]),
    status_ar: str(r["status_ar"]),
    status_raw: strOrNull(r["status_raw"]),
    notes_ar: strOrNull(r["notes_ar"]),
    source_row_no: typeof r["source_row_no"] === "number" ? r["source_row_no"] : null,
    data_quality: quality(r["data_quality"]),
  };
}

export type PaymentsMeta = {
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
  linkedCount: number;
  unlinkedCount: number;
  linkedTotal: string;
  unlinkedTotal: string;
};

export type ListPaymentsOpts = {
  q?: string;
  month?: string;
  channel?: string;
  /** `false` = اللي لسه مش منسوب لأسرة — ده الفلتر الأساسي للشاشة */
  linked?: boolean;
  page?: number;
  perPage?: number;
};

export async function listPayments(
  opts: ListPaymentsOpts = {},
): Promise<{ rows: PaymentRow[]; meta: PaymentsMeta }> {
  const path = "/payments";
  const res = await api.get<unknown>(path, {
    query: {
      q: opts.q || undefined,
      month: opts.month || undefined,
      channel: opts.channel || undefined,
      linked: opts.linked === undefined ? undefined : String(opts.linked),
      page: opts.page ?? 1,
      per_page: opts.perPage ?? 25,
    },
  });
  if (!Array.isArray(res.data)) fail(path, "قايمة المتحصلات رجعت بشكل مش متوقع");
  const m = obj(res.meta);
  return {
    rows: (res.data as unknown[]).map(paymentRow),
    meta: {
      page: num(m["page"], 1),
      perPage: num(m["per_page"], 25),
      total: num(m["total"]),
      totalPages: num(m["total_pages"], 1),
      linkedCount: num(m["linked_count"]),
      unlinkedCount: num(m["unlinked_count"]),
      linkedTotal: str(m["linked_total"]) || "0",
      unlinkedTotal: str(m["unlinked_total"]) || "0",
    },
  };
}

export type LinkResult = { linked: number; alsoLinked: number; familyNameAr: string; toastAr: string };

/**
 * ربط دفعة بأسرة.
 * 🔑 `applyToSameName` (افتراضيًا مفعّل) بيربط كل الدفعات **غير المربوطة**
 *    اللي اسمها الخام مطابق حرفيًا. ده اللي بيخلّي المهمة قابلة للإنجاز:
 *    ٢٣٦ هجاء بدل ٤٠٦ ضغطة.
 */
export async function linkPayment(
  id: string,
  familyId: string,
  applyToSameName = true,
): Promise<LinkResult> {
  const res = await api.post<unknown>(`/payments/${id}/link`, {
    family_id: familyId,
    apply_to_same_name: applyToSameName,
  });
  const d = obj(res.data);
  return {
    linked: num(d["linked"], 1),
    alsoLinked: num(d["also_linked"]),
    familyNameAr: str(d["family_name_ar"]),
    toastAr: str(d["toast_ar"]) || "اترَبطت",
  };
}

export async function unlinkPayment(id: string): Promise<string> {
  const res = await api.post<unknown>(`/payments/${id}/unlink`);
  return str(obj(res.data)["toast_ar"]) || "الربط اتفك";
}

/* ═════════════════════════════ الرواتب ═════════════════════════════ */

export type PayoutDestination = {
  method_ar: string;
  method_raw: string | null;
  bank_name_ar: string | null;
  /** `null` ممكن تعني **محجوب عنك** — اقرا `account_present` قبل ما تقول «مفيش» */
  account_raw: string | null;
  account_present: boolean;
};

function dest(v: unknown): PayoutDestination {
  const d = obj(v);
  return {
    method_ar: str(d["method_ar"]),
    method_raw: strOrNull(d["method_raw"]),
    bank_name_ar: strOrNull(d["bank_name_ar"]),
    account_raw: strOrNull(d["account_raw"]),
    account_present: d["account_present"] === true,
  };
}

export type TeacherPayoutRow = {
  id: string;
  month: string;
  teacher_name_raw: string;
  lines: { hours: string; hourly_rate: string; gross_usd: string; net_usd: string; hours_raw: string | null }[];
  total_net_usd: string;
  exchange_rate: string | null;
  total_egp: string | null;
  sent_egp: string | null;
  destinations: PayoutDestination[];
  notes_ar: string | null;
  data_quality: FinanceQuality;
};

function teacherRow(v: unknown): TeacherPayoutRow {
  const r = obj(v);
  return {
    id: str(r["id"]),
    month: str(r["month"]),
    teacher_name_raw: str(r["teacher_name_raw"]),
    lines: list(r["lines"]).map((l) => {
      const x = obj(l);
      return {
        hours: str(x["hours"]) || "0",
        hourly_rate: str(x["hourly_rate"]) || "0",
        gross_usd: str(x["gross_usd"]) || "0",
        net_usd: str(x["net_usd"]) || "0",
        hours_raw: strOrNull(x["hours_raw"]),
      };
    }),
    total_net_usd: str(r["total_net_usd"]) || "0",
    exchange_rate: strOrNull(r["exchange_rate"]),
    total_egp: strOrNull(r["total_egp"]),
    sent_egp: strOrNull(r["sent_egp"]),
    destinations: list(r["destinations"]).map(dest),
    notes_ar: strOrNull(r["notes_ar"]),
    data_quality: quality(r["data_quality"]),
  };
}

export type StaffPayoutRow = {
  id: string;
  month: string;
  staff_name_raw: string;
  base_egp: string;
  bonus_egp: string;
  withdrawal_fee_egp: string;
  deduction_egp: string;
  total_egp: string;
  destination: PayoutDestination | null;
  notes_ar: string | null;
  data_quality: FinanceQuality;
};

function staffRow(v: unknown): StaffPayoutRow {
  const r = obj(v);
  return {
    id: str(r["id"]),
    month: str(r["month"]),
    staff_name_raw: str(r["staff_name_raw"]),
    base_egp: str(r["base_egp"]) || "0",
    bonus_egp: str(r["bonus_egp"]) || "0",
    withdrawal_fee_egp: str(r["withdrawal_fee_egp"]) || "0",
    deduction_egp: str(r["deduction_egp"]) || "0",
    total_egp: str(r["total_egp"]) || "0",
    destination: r["destination"] ? dest(r["destination"]) : null,
    notes_ar: strOrNull(r["notes_ar"]),
    data_quality: quality(r["data_quality"]),
  };
}

export type PayoutsMeta = { page: number; perPage: number; total: number; totalPages: number; sum: string };

export async function listTeacherPayouts(
  opts: { q?: string; month?: string; page?: number; perPage?: number } = {},
): Promise<{ rows: TeacherPayoutRow[]; meta: PayoutsMeta }> {
  const path = "/teacher-payouts";
  const res = await api.get<unknown>(path, {
    query: { q: opts.q || undefined, month: opts.month || undefined,
             page: opts.page ?? 1, per_page: opts.perPage ?? 25 },
  });
  if (!Array.isArray(res.data)) fail(path, "قايمة رواتب المعلمين رجعت بشكل مش متوقع");
  const m = obj(res.meta);
  return {
    rows: (res.data as unknown[]).map(teacherRow),
    meta: { page: num(m["page"], 1), perPage: num(m["per_page"], 25),
            total: num(m["total"]), totalPages: num(m["total_pages"], 1),
            sum: str(m["sum_net_usd"]) || "0" },
  };
}

export async function listStaffPayouts(
  opts: { q?: string; month?: string; page?: number; perPage?: number } = {},
): Promise<{ rows: StaffPayoutRow[]; meta: PayoutsMeta }> {
  const path = "/staff-payouts";
  const res = await api.get<unknown>(path, {
    query: { q: opts.q || undefined, month: opts.month || undefined,
             page: opts.page ?? 1, per_page: opts.perPage ?? 25 },
  });
  if (!Array.isArray(res.data)) fail(path, "قايمة رواتب الموظفين رجعت بشكل مش متوقع");
  const m = obj(res.meta);
  return {
    rows: (res.data as unknown[]).map(staffRow),
    meta: { page: num(m["page"], 1), perPage: num(m["per_page"], 25),
            total: num(m["total"]), totalPages: num(m["total_pages"], 1),
            sum: str(m["sum_egp"]) || "0" },
  };
}

/* ═════════════════════════════ الملخّص ═════════════════════════════ */

export type FinanceSummary = {
  /** ⚠️ **الهدايا لوحدها**: دفتر الأكاديمية ما بيحسبهاش في «الاجمالى» */
  months: { month: string; count: number; revenue: string; gifts: string }[];
  paymentsCount: number;
  unlinkedCount: number;
  unlinkedTotal: string;
  teacherPayoutsCount: number;
  teacherPayoutsNetUsd: string;
  staffPayoutsCount: number;
  staffPayoutsEgp: string;
};

export async function getFinanceSummary(): Promise<FinanceSummary> {
  const path = "/finance/summary";
  const res = await api.get<unknown>(path);
  if (!res.data || typeof res.data !== "object") fail(path, "الملخّص المالي رجع فاضي");
  const d = obj(res.data);
  return {
    months: list(d["months"]).map((m) => {
      const x = obj(m);
      return { month: str(x["month"]), count: num(x["count"]),
               revenue: str(x["revenue"]) || "0", gifts: str(x["gifts"]) || "0" };
    }),
    paymentsCount: num(d["payments_count"]),
    unlinkedCount: num(d["unlinked_count"]),
    unlinkedTotal: str(d["unlinked_total"]) || "0",
    teacherPayoutsCount: num(d["teacher_payouts_count"]),
    teacherPayoutsNetUsd: str(d["teacher_payouts_net_usd"]) || "0",
    staffPayoutsCount: num(d["staff_payouts_count"]),
    staffPayoutsEgp: str(d["staff_payouts_egp"]) || "0",
  };
}

/* ═════════════════════════ رفع الدفاتر ═════════════════════════ */

export type BookReport = {
  key: string;
  labelAr: string;
  records: number;
  months: { month: string; computed: string; declared: string | null; diff: string | null }[];
  tagged: Record<string, number>;
  warnings: string[];
  skipped: string[];
};

export type ImportPreview = {
  mode: "dry_run" | "applied";
  fileHash: string;
  books: BookReport[];
  written: Record<string, number> | null;
  linked: Record<string, Record<string, number>> | null;
};

const BOOK_LABELS: Record<string, string> = {
  payments: "المتحصلات",
  teacher_payouts: "رواتب المعلمين",
  staff_payouts: "رواتب الموظفين",
};

function preview(v: unknown): ImportPreview {
  const d = obj(v);
  const books = obj(d["books"]);
  return {
    mode: str(d["mode"]) === "applied" ? "applied" : "dry_run",
    fileHash: str(d["file_hash"]),
    books: Object.keys(books).map((k) => {
      const b = obj(books[k]);
      return {
        key: k,
        labelAr: BOOK_LABELS[k] || k,
        records: num(b["records"]),
        months: list(b["months"]).map((m) => {
          const x = obj(m);
          return {
            month: str(x["month"]),
            computed: str(x["computed"]) || "0",
            declared: strOrNull(x["declared"]),
            diff: strOrNull(x["diff"]),
          };
        }),
        tagged: Object.fromEntries(
          Object.entries(obj(b["tagged"])).map(([kk, vv]) => [kk, num(vv)]),
        ),
        warnings: list(b["warnings"]).map(str).filter(Boolean),
        skipped: list(b["skipped"]).map(str).filter(Boolean),
      };
    }),
    written: d["written"] ? Object.fromEntries(
      Object.entries(obj(d["written"])).map(([k, v2]) => [k, num(v2)])) : null,
    linked: d["linked"] ? Object.fromEntries(
      Object.entries(obj(d["linked"])).map(([k, v2]) => [
        k, Object.fromEntries(Object.entries(obj(v2)).map(([kk, vv]) => [kk, num(vv)])),
      ])) : null,
  };
}

/**
 * بيرفع الدفاتر — **معاينة أولًا دايمًا**.
 *
 * 🔒 الكتابة لازم تجيب `confirmHash` اللي المعاينة رجّعته. يعني مستحيل حد
 *    يكتب دفترًا محدش شاف أرقامه، ولو الملف اتغيّر بين المعاينة والتطبيق
 *    البصمة ما بتطابقش والكتابة بتترفض.
 */
export async function uploadFinanceBooks(
  payload: unknown,
  opts: { apply?: boolean; confirmHash?: string } = {},
): Promise<ImportPreview> {
  const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
  const form = new FormData();
  form.append("file", blob, "books.json");
  form.append("apply", String(Boolean(opts.apply)));
  if (opts.confirmHash) form.append("confirm_hash", opts.confirmHash);
  const res = await api.post<unknown>("/imports/finance", form);
  if (!res.data || typeof res.data !== "object") fail("/imports/finance", "الرد فاضي");
  return preview(res.data);
}
